/**
 * Acxiom Label Mapper
 *
 * This module transforms raw Acxiom response data (flat key-value format) into
 * structured and readable segment data, using internal normalized keys and value label mappings.
 *
 * The API response from Acxiom is a flat object where each key is a trait code (e.g., 'AN', 'JF', 'JT'),
 * and values are encoded strings that require mapping to human-readable labels.
 *
 * This module:
 * - Maps Acxiom trait codes to internal `a_`-prefixed property names.
 * - Converts encoded values (when applicable) into descriptive labels.
 * - Supports multi-code traits (e.g., age ranges, generational data).
 *
 * Public Method:
 * - mapAcxiomTrackingProperties(rawData): shortcut that performs both steps.
 *   Returns an object with normalized internal keys and human-readable labels.
 *
 * This is part of the data processing pipeline to convert vendor-locked Acxiom data
 * into segment-readable output used for analytics or personalization.
 */

const AXIOM_LABEL_MAP = {
  a_childagerange: (value) => {
    const values = {
      HQ: '00-02-Male',
      HR: '00-02-Female',
      HS: '00-02-UnknownGender',
      HT: '03-05-Male',
      HU: '03-05-Female',
      HV: '03-05-UnknownGender',
      HW: '06-10-Male',
      HX: '06-10-Female',
      HY: '06-10-UnknownGender',
      HZ: '11-15-Male',
      IA: '11-15-Female',
      IB: '11-15-UnknownGender',
      IC: '16-17-Male',
      ID: '16-17-Female',
      IE: '16-17-UnknownGender',
      IF: 'precisionLevel'
    };

    return value
      .split(',')
      .map((code) => values[code.trim()] || code.trim())
      .join(',');
  },
  a_gender: (value) => {
    const values = {
      M: 'Male',
      F: 'Female',
    };
    return values[value] || value;
  },
  a_householdsize: (value) => {
    const values = {
      1: '1 Person in the Household',
      2: '2 People in the Household',
      3: '3 People in the Household',
      4: '4 People in the Household',
      5: '5 People in the Household',
      6: '6 People in the Household',
      7: '7 People in the Household',
      8: '8 People in the Household',
      9: 'Greater than 8 People in the Household',
    };
    return values[value] || value;
  },
  a_age: (value) => {
    const values = {
      cv1: 'Age less than 18',
      fg2: 'Age 18 - 19',
      ev4: 'Age 20 - 21',
      '9gx': 'Age 22 - 23',
      tr2: 'Age 24 - 25',
      da6: 'Age 26 - 27',
      '3h5': 'Age 28 - 29',
      '8xr': 'Age 30 - 31',
      '5my': 'Age 32 - 33',
      c9g: 'Age 34 - 35',
      '6t5': 'Age 36 - 37',
      yv4: 'Age 38 - 39',
      v9k: 'Age 40 - 41',
      r48: 'Age 42 - 43',
      '66c': 'Age 44 - 45',
      x54: 'Age 46 - 47',
      t98: 'Age 48 - 49',
      '3v2': 'Age 50 - 51',
      vh9: 'Age 52 - 53',
      pk8: 'Age 54 - 55',
      nu2: 'Age 56 - 57',
      a8b: 'Age 58 - 59',
      '7mq': 'Age 60 - 61',
      '6wg': 'Age 62 - 63',
      ft7: 'Age 64 - 65',
      kc3: 'Age 66 - 67',
      f6r: 'Age 68 - 69',
      '8um': 'Age 70 - 71',
      '8nh': 'Age 72 - 73',
      q6b: 'Age 74 - 75',
      k6g: 'Age 76 - 77',
      pc5: 'Age 78 - 79',
      tz7: 'Age 80 - 81',
      '2ug': 'Age 82 - 83',
      '4dz': 'Age 84 - 85',
      af3: 'Age 86 - 87',
      ub6: 'Age 88 - 89',
      qc7: 'Age 90 - 91',
      '2ez': 'Age 92 - 93',
      '6af': 'Age 94 - 95',
      '7ph': 'Age 96 - 97',
      ed9: 'Age 98 - 99',
      d6p: 'Age greater than 99'
    };
    return values[value] || value;
  },
  a_householdgenr: (value) => {
    const values = {
      1: '1 Generation - 1 Adult',
      2: '2 Generations - Adult-Child',
      3: '3 Generations - Adult-Child-Parent'
    };
    return values[value] || value;
  },
  a_maritalstatus: (value) => {
    const values = {
      M: 'Married',
      S: 'Single',
      A: 'Inferred Married',
      B: 'Inferred Single'
    };
    return values[value] || value;
  },
  a_lifestage: (value) => {
    const values = {
      '01Y': 'Launching Life',
      '02Y': 'Gaining Traction',
      '03Y': 'Ascending Ambitions',
      '04Y': 'Establishing Foundations',
      '05C': 'Modest Transitions',
      '06C': 'Singularly Focused',
      '07C': 'Taking Flight',
      '08C': 'Studio Singles',
      '09C': 'Established Sophisticates',
      '10C': 'Freewheeling Freelancers',
      '11E': 'Active & Entertained',
      '12E': 'Casual Couples',
      '13E': 'Scenic & Settled',
      '14E': 'Limitless Luxury',
      '15E': 'Loving Lunchables',
      '16E': 'Prosperous Pastimes',
      '17E': 'Pastoral Upbringings',
      '18E': 'Practical Priorities',
      '19M': 'Modest Metronites',
      '20M': 'Suburban Stability',
      '21M': 'Comfortable Companions',
      '22M': 'Modest Maturity',
      '23M': 'Sunset Series'
    };
    return values[value] || value;
  },
  n_zip: (value) => {
    if (typeof value === 'string' && value.length === 9) {
      return `${value.slice(0, 5)}-${value.slice(5)}`;
    }
    return value;
  },
  a_propertytype: (value) => {
    const values = {
      A: 'Single Family Dwelling Unit',
      B: 'Condo',
      C: 'Cooperative',
      D: '2-4 Unit – Duplex/Triplex/Quad',
      E: 'Miscellaneous Residence – Combo Store/Flat',
      G: 'Apartment (5+ Units)',
      M: 'Mobile Home',
      T: 'Timeshare'
    };
    return values[value] || value;
  },
  a_yearhomebuilt: (value) => {
    const values = {
      A: 'Before 1900',
      B: '1900 - 1940',
      C: '1941 - 1950',
      D: '1951 - 1960',
      E: '1961 - 1970',
      F: '1971 - 1980',
      G: '1981 - 1985',
      H: '1986 - 1990',
      I: '1991 - 1995',
      J: '1996 - 2000',
      K: '2001 - 2005',
      L: '2006 - 2010',
      M: '2011 - 2015',
      N: '2016 - 2020',
      O: '2021 - 2025',
      P: '2026 and greater'
    };
    return values[value] || value;
  },
  a_adultagerange: (value) => {
    const values = {
      IH: '18-24-Female',
      II: '18-24-Male',
      IJ: '18-24-UnknownGender',
      IK: '25-34-Female',
      IL: '25-34-Male',
      IM: '25-34-UnknownGender',
      IN: '35-44-Female',
      IO: '35-44-Male',
      IP: '35-44-UnknownGender',
      IQ: '45-54-Female',
      IR: '45-54-Male',
      IS: '45-54-UnknownGender',
      IT: '55-64-Female',
      IU: '55-64-Male',
      IV: '55-64-UnknownGender',
      IW: '65-74-Female',
      IX: '65-74-Male',
      IY: '65-74-UnknownGender',
      IZ: '75Plus-Female',
      JA: '75Plus-Male',
      JB: '75Plus-UnknownGender'
    };

    return value
      .split(',')
      .map((code) => values[code.trim()] || code.trim())
      .join(',');
  },
  a_homeownership: (value) => {
    const values = {
      O: 'Home Owner',
      R: 'Renter'
    };

    return values[value] || value;
  },
  a_residenceduration: (value) => {
    const values = {
      '00': 'Less than 1 Year',
      '01': '1 Year',
      '02': '2 Years',
      '03': '3 Years',
      '04': '4 Years',
      '05': '5 Years',
      '06': '6 Years',
      '07': '7 Years',
      '08': '8 Years',
      '09': '9 Years',
      10: '10 Years',
      11: '11 Years',
      12: '12 Years',
      13: '13 Years',
      14: '14 Years',
      15: 'Greater than 14 Years'
    };

    return values[value] || value;
  },
  a_housingtype: (value) => {
    const values = {
      M: 'Multiple Family Dwelling Unit',
      S: 'Single Family Dwelling Unit'
    };

    return values[value] || value;
  },
  a_housholdincome: (value) => {
    const values = {
      1: 'Less than $15,000',
      2: '$15,000 - $19,999',
      3: '$20,000 - $29,999',
      4: '$30,000 - $39,999',
      5: '$40,000 - $49,999',
      6: '$50,000 - $59,999',
      7: '$60,000 - $69,999',
      8: '$70,000 - $79,999',
      9: '$80,000 - $89,999',
      A: '$90,000 - $99,999',
      B: '$100,000 - $124,999',
      C: '$125,000 - $149,999',
      D: 'Greater than $149,999'
    };

    return values[value] || value;
  },
  a_homemarketvalue: (value) => {
    const values = {
      A: 'Less than $50,000',
      B: '$50,000 - $99,999',
      C: '$100,000 - $149,999',
      D: '$150,000 - $199,999',
      E: '$200,000 - $249,999',
      F: '$250,000 - $299,999',
      G: '$300,000 - $349,999',
      H: '$350,000 - $399,999',
      I: '$400,000 - $449,999',
      J: '$450,000 - $499,999',
      K: '$500,000 - $599,999',
      L: '$600,000 - $699,999',
      M: '$700,000 - $799,999',
      N: '$800,000 - $999,999',
      O: '$1,000,000 - $1,499,999',
      P: '$1,500,000 - $1,999,999',
      Q: 'Greater than $1,999,999'
    };

    return values[value] || value;
  },
  a_ishomefurnishing: (value) => {
    const values = {
      1: 'True'
    };
    return values[value] || value;
  },
  a_affordability: (value) => {
    const values = {
      '01': 'Cushioned Comfort - Very Comfortable cushion',
      '02': 'Cushioned Comfort',
      '03': 'Cushioned Comfort',
      '04': 'Cushioned Comfort',
      '05': 'Cushioned Comfort',
      '06': 'Cushioned Comfort',
      '07': 'Cushioned Comfort',
      '08': 'Cushioned Comfort',
      '09': 'Cushioned Comfort',
      10: 'Cushioned Comfort - Comfortable cushion but stretched',
      11: 'Keeping Pace - Acceptable cushion',
      12: 'Keeping Pace',
      13: 'Keeping Pace',
      14: 'Keeping Pace',
      15: 'Keeping Pace - Acceptable cushion but cautious',
      16: 'Keeping Pace',
      17: 'Keeping Pace',
      18: 'Keeping Pace',
      19: 'Keeping Pace',
      20: 'Keeping Pace - Acceptable cushion but stretched',
      21: 'No Frills - Uncomfortable cushion',
      22: 'No Frills',
      23: 'No Frills',
      24: 'No Frills',
      25: 'No Frills - uncomfortable over stretched cushion',
      26: 'No Frills',
      27: 'No Frills',
      28: 'No Frills',
      29: 'No Frills',
      30: 'No Frills - Very uncomfortable cushion',
    };
    return values[value] || value;
  },
  a_isaffluentcouple: (value) => {
    const values = {
      1: 'True'
    };
    return values[value] || value;
  },
  a_isaffluentowner: (value) => {
    const values = {
      1: 'True'
    };
    return values[value] || value;
  },
  a_homesqft: (value) => {
    const values = {
      m3n: '0000000-0001249',
      l3y: '0001250-00001499',
      z3h: '00001500-00001749',
      e3g: '00001750-00001999',
      s3g: '00002000-00002499',
      w3d: '00002500-00002999',
      s3u: '00003000-00003499',
      w1x: '00003500-00003999',
      i9v: '00004000-00004999',
      c3o: '00005000-00005999',
      v9b: '00006000-00006999',
      i3v: '00007000-99999999',
    };

    return values[value] || value;
  },
  a_mortgagebalance: (value) => {
    const values = {
      '8c6': '000000400000 - 000000499999',
      yk3: '000000050000 - 000000074999',
      e3m: '000000005001 - 000000009999',
      p3s: '000000010000 - 000000019999',
      m3q: '000001000000 - 000001999999',
      b3f: '000000030000 - 000000049999',
      '5x1': '000000350000 - 000000399999',
      r3h: '000000020000 - 000000029999',
      '5l2': '000000250000 - 000000299999',
      g3d: '000000500000 - 000000749999',
      '7d1': '000000100000 - 000000149999',
      '1p2': '000000200000 - 000000249999',
      f3w: '000000150000 - 000000199999',
      x3m: '000000075000 - 000000099999',
      '4i1': '000000000001 - 000000005000',
      d0q: '000000300000 - 000000349999',
      '5a1': '000000750000 - 000000999999',
      '3i4': '000002000000 - 000997422694',
    };

    return values[value] || value;
  },
  a_networthgold: (value) => {
    const values = {
      1: 'Less than or equal to $0',
      2: '$1 - $4,999',
      3: '$5,000 - $9,999',
      4: '$10,000 - $24,999',
      5: '$25,000 - $49,999',
      6: '$50,000 - $99,999',
      7: '$100,000 - $249,999',
      8: '$250,000 - $499,999',
      9: '$500,000 - $999,999',
      A: '$1,000,000 - $1,999,999',
      B: '$2,000,000 +'
    };

    return values[value] || value;
  },
  a_numchildren: (value) => {
    const values = {
      0: 'No children',
      1: '1 Child',
      2: '2 Children',
      3: '3 Children',
      4: '4 Children',
      5: '5 Children',
      6: '6 Children',
      7: '7 Children',
      8: 'Greater than 7 Children',
    };

    return values[value] || value;
  },
};

/**
 * Transforms raw Acxiom data into a structured payload object with mapped property names.
 * @returns {Object|null} The structured payload object with mapped Acxiom data,
 * or null if input is falsy.
 */
function getAcxiomDataValues(acxiomRawData) {
  if (!acxiomRawData) { return null; }

  const payload = {
    a_haschildren: acxiomRawData.AI,
    a_age: acxiomRawData.AN,
    a_maritalstatus: acxiomRawData.BF,
    a_householdgenr: acxiomRawData.BH,
    a_housingtype: acxiomRawData.BJ,
    a_propertytype: acxiomRawData.DF,
    a_numchildren: acxiomRawData.IG,
    a_gender: acxiomRawData.JN,
    a_householdsize: acxiomRawData.JD,
    a_yearhomebuilt: acxiomRawData.JE,
    a_housholdincome: acxiomRawData.JF,
    a_homemarketvalue: acxiomRawData.JG,
    a_isaffluentowner: acxiomRawData.JH,
    a_homesqft: acxiomRawData.JI,
    a_homepurchasedate: acxiomRawData.JK,
    a_firsthomeloandate: acxiomRawData.JL,
    a_networthgold: acxiomRawData.JM,
    a_homeownership: acxiomRawData.JO,
    a_residenceduration: acxiomRawData.JC,
    a_affordability: acxiomRawData.JP,
    a_ishomefurnishing: acxiomRawData.JQ,
    a_mortgagebalance: acxiomRawData.JR,
    a_segment: acxiomRawData.JS,
    a_lifestage: acxiomRawData.JT,
    a_isaffluentcouple: acxiomRawData.AA,
    a_isrecentbuyer: acxiomRawData.HL,
    a_hasrecentmortgage: acxiomRawData.HM,
    a_parcel_number: acxiomRawData.JJ,
    n_zip: acxiomRawData.JU,
    SIGNAL_SCORE: acxiomRawData.SIGNAL_SCORE,
  };

  const childAgeCodes = [
    'HQ', 'HR', 'HS', 'HT', 'HU', 'HV', 'HW', 'HX', 'HY', 'HZ',
    'IA', 'IB', 'IC', 'ID', 'IE', 'IF'
  ];
  const childAgeValues = childAgeCodes
    .filter((code) => acxiomRawData[code])
    .map((code) => code);
  if (childAgeValues.length > 0) {
    payload.a_childagerange = childAgeValues.join(',');
  }

  const adultAgeCodes = [
    'IH', 'II', 'IJ', 'IK', 'IL', 'IM', 'IN', 'IO', 'IP',
    'IQ', 'IR', 'IS', 'IT', 'IU', 'IV', 'IW', 'IX', 'IY',
    'IZ', 'JA', 'JB'
  ];

  const adultAgeValues = adultAgeCodes
    .filter((code) => acxiomRawData[code])
    .map((code) => code);
  if (adultAgeValues.length > 0) {
    payload.a_adultagerange = adultAgeValues.join(',');
  }

  return payload;
}

/**
 * Maps Acxiom data values to their corresponding labels using AXIOM_LABEL_MAP.
 * @param {Object} acxiomDataValues - An object containing Acxiom data key-value pairs.
 * @returns {Object|null} An object with the same keys as acxiomDataValues,
 * where each value is replaced by its label if a mapping function exists,
 * or the original value otherwise. Returns null if acxiomDataValues is falsy.
 */
function getLabeledTrackingProperties(acxiomDataValues) {
  if (!acxiomDataValues) { return null; }

  const datawithLabels = {};

  Object.entries(acxiomDataValues).forEach(([key, value]) => {
    const labelMapping = AXIOM_LABEL_MAP[key];
    datawithLabels[key] = typeof labelMapping === 'function' ? labelMapping(value) : value;
  });

  return datawithLabels;
}

/**
 * An object or array containing tracking properties that have been labeled
 * based on the provided Acxiom data values.
 *
 * @type {Object|Array}
 * @see getLabeledTrackingProperties
 */
function mapAcxiomTrackingProperties(rawData) {
  const acxiomDataValues = getAcxiomDataValues(rawData);
  const labeledTrackingProperties = getLabeledTrackingProperties(acxiomDataValues);
  return labeledTrackingProperties;
};

export default mapAcxiomTrackingProperties;